import * as Sentry from "@sentry/react";
import { FeatureArea } from "utils/sentry/inferFeatureArea";
import { sanitizeDeep, sanitizeRoute, sanitizeRouteOrLabel } from "utils/privacy/telemetryUrl";

export type ErrorSource = "http" | "websocket" | "render" | "manual";
export type ReportLevel = "error" | "warning" | "info";

export interface ReportErrorOptions {
  source: ErrorSource;
  featureArea: FeatureArea;
  level?: ReportLevel;
  httpStatus?: number;
  requestPath?: string;
  requestId?: string;
  route?: string;
  method?: string;
  fingerprint?: string[];
  extras?: Record<string, unknown>;
}

const isSentryEnabled = (): boolean => Sentry.getClient() !== undefined;

const applyScope = (scope: Sentry.Scope, opts: ReportErrorOptions) => {
  scope.setLevel(opts.level ?? "error");
  scope.setTags({
    feature_area: opts.featureArea,
    error_source: opts.source,
    ...(opts.httpStatus !== undefined && { http_status: String(opts.httpStatus) }),
  });

  // Privacy B1 (REF-6708): sanitised at the source as well; `scrubEvent` in beforeSend is the guarantee.
  const extras: Record<string, unknown> = {
    ...sanitizeDeep(opts.extras),
  };

  if (opts.requestPath) extras.request_path = sanitizeRoute(opts.requestPath);
  if (opts.requestId) extras.request_id = opts.requestId;
  if (opts.route) extras.route = sanitizeRoute(opts.route);
  if (opts.method) extras.method = opts.method;

  scope.setExtras(extras);

  if (opts.fingerprint && opts.fingerprint.length > 0) {
    scope.setFingerprint(opts.fingerprint.map(sanitizeRouteOrLabel));
  }
};

export const reportError = (error: unknown, opts: ReportErrorOptions): void => {
  if (!isSentryEnabled()) return;

  Sentry.withScope((scope) => {
    applyScope(scope, opts);
    Sentry.captureException(error);
  });
};

export const reportMessage = (message: string, opts: ReportErrorOptions): void => {
  if (!isSentryEnabled()) return;

  Sentry.withScope((scope) => {
    applyScope(scope, opts);
    Sentry.captureMessage(message);
  });
};
